import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

const CustomMultiSelectDropdown = ({
  label,
  options = [],
  value = [],
  onChange,
  name,
  disabled = false,
  size = "medium",
  isMultiSelect = true,
}) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const themeColor = "var(--primary-color, #1D7A67)";

  const sizeStyles = {
    small: { fontSize: "0.8rem", padding: "4px 8px", minHeight: "28px" },
    medium: { fontSize: "1rem", padding: "6px 10px", minHeight: "30px" },
    large: { fontSize: "1.25rem", padding: "8px 12px", minHeight: "36px" },
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const modalEl = document.getElementById("custom-dropdown-modal");
        if (modalEl && modalEl.contains(event.target)) return;
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (val) => {
    if (isMultiSelect) {
      if (value.includes(val)) {
        onChange(value.filter((v) => v !== val));
      } else {
        onChange([...value, val]);
      }
    } else {
      onChange(val);
      setIsOpen(false);
    }
  };

  const displayText = isMultiSelect
    ? value.length === 0
      ? "-- Select --"
      : options
          .filter((opt) => value.includes(opt.value))
          .map((opt) => opt.label)
          .join(", ")
    : value
    ? options.find((opt) => opt.value === value)?.label || "-- Select --"
    : "-- Select --";

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      ref={dropdownRef}
      className="dropdown-container"
      style={{ marginBottom: "1rem", width: "100%", maxWidth: "400px", position: "relative" }}
    >
      {label && (
        <label
          htmlFor={name}
          style={{
            fontWeight: "600",
            fontSize: "1rem",
            color: "#333",
            marginBottom: "0.25rem",
            display: "block",
          }}
        >
          {label}
        </label>
      )}

      {/* Visible dropdown box */}
      <div
        onClick={() => !disabled && setIsOpen(true)}
        className="dropdown-select"
        style={{
          padding: sizeStyles[size].padding,
          fontSize: sizeStyles[size].fontSize,
          border: "1.5px solid #ccc",
          borderRadius: "5px",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#f5f5f5" : "white",
          minHeight: sizeStyles[size].minHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{displayText}</span>
        <span>{isOpen ? "▲" : "▼"}</span>
      </div>

      {/* Modal dialog */}
      {isOpen &&
        !disabled &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px",
              zIndex: 12000,
            }}
            onClick={() => setIsOpen(false)}
          >
          <div
            id="custom-dropdown-modal"
            style={{
              backgroundColor: "white",
              padding: "1.5rem",
              borderRadius: "10px",
              width: "500px",
              maxWidth: "100%",       // 🔹 fit inside viewport
              boxSizing: "border-box", // 🔹 include padding in width
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
              {/* Dialog Title */}
              <div
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  marginBottom: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: themeColor,
                }}
              >
                <span>Select Options</span>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: "1.2rem",
                    cursor: "pointer",
                  }}
                >
                  ✖
                </button>
              </div>

              {/* Search input */}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Search..."
                style={{
                  width: "90%",
                  padding: "8px 10px",
                  border: `2px solid ${isFocused ? themeColor : "#ccc"}`,
                  marginBottom: "12px",
                  fontSize: "1rem",
                  borderRadius: "5px",
                  outline: "none",
                }}
              />

              {/* Options */}
              <div style={{ flexGrow: 1 }}>
                {filteredOptions.length === 0 && <p>No options found</p>}
                {filteredOptions.map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    {isMultiSelect ? (
                      <input
                        type="checkbox"
                        checked={value.includes(opt.value)}
                        onChange={() => toggleOption(opt.value)}
                        style={{ marginRight: "8px" }}
                      />
                    ) : (
                      <input
                        type="radio"
                        name={name}
                        checked={value === opt.value}
                        onChange={() => toggleOption(opt.value)}
                        style={{ marginRight: "8px" }}
                      />
                    )}
                    {opt.label}
                  </label>
                ))}
              </div>

              {/* Footer button */}
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  marginTop: "15px",
                  padding: "10px 25px",
                  borderRadius: "18px",
                  border: "none",
                  backgroundColor: themeColor,
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "bold",
                  alignSelf: "center",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomMultiSelectDropdown;
